import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ========== Tenant Management ==========

export const listTenants = async (params = {}) => {
  const response = await api.get('/api/admin/tenants', { params });
  return response.data;
};

export const getTenant = async (tenantId) => {
  const response = await api.get(`/api/admin/tenants/${tenantId}`);
  return response.data;
};

export const createTenant = async (data) => {
  const response = await api.post('/api/admin/tenants', data);
  return response.data;
};

export const updateTenant = async (tenantId, data) => {
  const response = await api.put(`/api/admin/tenants/${tenantId}`, data);
  return response.data;
};

export const deleteTenant = async (tenantId) => {
  const response = await api.delete(`/api/admin/tenants/${tenantId}`);
  return response.data;
};

// ========== Tenant Members ==========

export const listTenantMembers = async (tenantId, params = {}) => {
  const response = await api.get(`/api/admin/tenants/${tenantId}/members`, { params });
  return response.data;
};

export const inviteMember = async (tenantId, data) => {
  const response = await api.post(`/api/admin/tenants/${tenantId}/members/invite`, data);
  return response.data;
};

export const updateMember = async (tenantId, userId, data) => {
  const response = await api.put(`/api/admin/tenants/${tenantId}/members/${userId}`, data);
  return response.data;
};

export const removeMember = async (tenantId, userId) => {
  const response = await api.delete(`/api/admin/tenants/${tenantId}/members/${userId}`);
  return response.data;
};

// ========== Invitations ==========

export const acceptInvitation = async (token) => {
  const response = await api.post(`/api/admin/invitations/${token}/accept`);
  return response.data;
};

// ========== Super Admins ==========

export const listSuperAdmins = async () => {
  const response = await api.get('/api/admin/super-admins');
  return response.data;
};

export const grantSuperAdmin = async (userId) => {
  const response = await api.post(`/api/admin/super-admins/${userId}`);
  return response.data;
};

export const revokeSuperAdmin = async (userId) => {
  const response = await api.delete(`/api/admin/super-admins/${userId}`);
  return response.data;
};

// ========== License & Modules ==========

export const getLicenseTiers = async () => {
  const response = await api.get('/api/admin/license-tiers');
  return response.data;
};

export const getModules = async () => {
  const response = await api.get('/api/admin/modules');
  return response.data;
};

export const updateTenantModules = async (tenantId, modules) => {
  const response = await api.put(`/api/admin/tenants/${tenantId}/modules`, modules);
  return response.data;
};

// ========== Roles & Permissions ==========

export const getRoles = async () => {
  const response = await api.get('/api/admin/roles');
  return response.data;
};

export const getPermissions = async () => {
  const response = await api.get('/api/admin/permissions');
  return response.data;
};

// ========== Current User Context ==========

export const getMyTenants = async () => {
  const response = await api.get('/api/admin/me/tenants');
  return response.data;
};

export const getMyPermissions = async (tenantId) => {
  const response = await api.get(`/api/admin/me/permissions/${tenantId}`);
  return response.data;
};

// ========== GRC Frameworks ==========

export const getAvailableFrameworks = async () => {
  const response = await api.get('/api/grc/available-frameworks');
  return response.data;
};

export const getTenantFrameworks = async (tenantId) => {
  const response = await api.get('/api/grc/tenant-frameworks', { 
    params: { tenant_id: tenantId } 
  });
  return response.data;
};

export const enableFramework = async (code, tenantId, owner = 'Security Team') => {
  const response = await api.post(`/api/grc/frameworks/enable/${code}`, null, {
    params: { tenant_id: tenantId, owner }
  });
  return response.data;
};

export const disableFramework = async (code, tenantId, deleteControls = false) => {
  const response = await api.delete(`/api/grc/frameworks/disable/${code}`, {
    params: { tenant_id: tenantId, delete_controls: deleteControls }
  });
  return response.data;
};

export const syncFrameworkControls = async (code, tenantId, owner = 'Security Team') => {
  const response = await api.post(`/api/grc/frameworks/sync/${code}`, null, {
    params: { tenant_id: tenantId, owner }
  });
  return response.data;
};

// ========== Helper Functions ==========

export const LICENSE_TIER_COLORS = {
  free: 'bg-gray-100 text-gray-700 border-gray-200',
  starter: 'bg-blue-100 text-blue-700 border-blue-200',
  professional: 'bg-purple-100 text-purple-700 border-purple-200',
  enterprise: 'bg-amber-100 text-amber-700 border-amber-200'
};

export const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700',
  owner: 'bg-purple-100 text-purple-700',
  team_member: 'bg-blue-100 text-blue-700',
  auditor: 'bg-amber-100 text-amber-700',
  viewer: 'bg-gray-100 text-gray-700'
};

export const MODULE_ICONS = {
  grc: '📊',
  osint: '🔍',
  litesabre: '⚔️',
  litesabre_pro: '🛡️',
  amidala: '☁️',
  sbom: '📦',
  dpdpa: '🔐'
};

export const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  suspended: 'bg-yellow-100 text-yellow-700',
  pending: 'bg-blue-100 text-blue-700',
  deactivated: 'bg-red-100 text-red-700'
};

// ========== New: Users & Revenue ==========

export const searchPlatformUsers = async (q) => {
  const response = await api.get('/api/admin/users/search', { params: { q } });
  return response.data;
};

export const listAllUsers = async (params = {}) => {
  const response = await api.get('/api/admin/users', { params });
  return response.data;
};

export const getPlatformStats = async () => {
  const response = await api.get('/api/admin/stats');
  return response.data;
};

export const updateTenantPricing = async (tenantId, data) => {
  const response = await api.put(`/api/admin/tenants/${tenantId}/pricing`, data);
  return response.data;
};

// ========== Visible Frameworks ==========

export const getTenantVisibleFrameworks = async (tenantId) => {
  const response = await api.get(`/api/admin/tenants/${tenantId}/visible-frameworks`);
  return response.data;
};

export const updateTenantVisibleFrameworks = async (tenantId, frameworks) => {
  const response = await api.put(`/api/admin/tenants/${tenantId}/visible-frameworks`, frameworks);
  return response.data;
};

// ========== Framework Requests ==========

export const listFrameworkRequests = async (params = {}) => {
  const response = await api.get('/api/admin/framework-requests', { params });
  return response.data;
};

export const updateFrameworkRequest = async (requestId, action) => {
  const response = await api.put(`/api/admin/framework-requests/${requestId}`, null, { params: { action } });
  return response.data;
};

// ========== Preview Modules ==========

export const addPreviewModules = async (userId, modules) => {
  const response = await api.post(`/api/admin/users/${userId}/preview-modules`, modules);
  return response.data;
};

export default {
  listTenants,
  getTenant,
  createTenant,
  updateTenant,
  deleteTenant,
  listTenantMembers,
  inviteMember,
  updateMember,
  removeMember,
  acceptInvitation,
  listSuperAdmins,
  grantSuperAdmin,
  revokeSuperAdmin,
  getLicenseTiers,
  getModules,
  updateTenantModules,
  getRoles,
  getPermissions,
  getMyTenants,
  getMyPermissions,
  searchPlatformUsers,
  listAllUsers,
  getPlatformStats,
  updateTenantPricing,
  getTenantVisibleFrameworks,
  updateTenantVisibleFrameworks,
  listFrameworkRequests,
  updateFrameworkRequest,
  addPreviewModules,
};

// ========== SAML SSO (per-tenant) ==========

export const getGoogleSigninConfig = async (tenantId) => {
  const response = await api.get(`/api/auth/google-signin/tenants/${tenantId}/config`);
  return response.data;
};

export const updateGoogleSigninConfig = async (tenantId, payload) => {
  const response = await api.put(`/api/auth/google-signin/tenants/${tenantId}/config`, payload);
  return response.data;
};

export const listGoogleAutojoinEvents = async (tenantId, q) => {
  const response = await api.get(`/api/auth/google-signin/tenants/${tenantId}/autojoin-events`, {
    params: q ? { q } : {},
  });
  return response.data;
};

export const getTenantSsoConfig = async (tenantId) => {
  const response = await api.get(`/api/saml/tenants/by-id/${tenantId}/config`);
  return response.data;
};

export const updateTenantSsoConfig = async (tenantId, payload) => {
  const response = await api.put(`/api/saml/tenants/by-id/${tenantId}/config`, payload);
  return response.data;
};

export const parseIdpMetadata = async (tenantId, { source, value }) => {
  const response = await api.post(
    `/api/saml/tenants/by-id/${tenantId}/parse-metadata`,
    { source, value },
  );
  return response.data;
};

// ========== Directory Sync (per-tenant, IdP pull) ==========

export const getTenantDirectoryConfig = async (tenantId) => {
  const response = await api.get(`/api/saml/tenants/by-id/${tenantId}/directory/config`);
  return response.data;
};

export const updateTenantDirectoryConfig = async (tenantId, payload) => {
  const response = await api.put(`/api/saml/tenants/by-id/${tenantId}/directory/config`, payload);
  return response.data;
};

export const triggerTenantDirectorySync = async (tenantId) => {
  const response = await api.post(`/api/saml/tenants/by-id/${tenantId}/directory/sync`);
  return response.data;
};

export const testTenantDirectoryConnection = async (tenantId, payload) => {
  const response = await api.post(`/api/saml/tenants/by-id/${tenantId}/directory/test`, payload);
  return response.data;
};

export const listTenantDirectoryRuns = async (tenantId) => {
  const response = await api.get(`/api/saml/tenants/by-id/${tenantId}/directory/runs`);
  return response.data;
};

